import {
  Alert,
  Box,
  CHART_COLOR_NAMES,
  Card,
  InfoDot,
  PieChart,
  Table,
  Text,
} from '@factorialco/deprecated-design-system'
import { AtsApplicationsGenderInsightsChartFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { getGenderLabel } from 'modules/ats/lib/utils/gender'

import { DataNotAvailable } from '../DataNotAvailable'

interface Props {
  applicationsGenderData: AtsApplicationsGenderInsightsChartFragment[]
  alert?: string
}

export const ApplicationsGenderChart = ({ applicationsGenderData, alert }: Props) => {
  const data = applicationsGenderData.map((insight, index) => ({
    color: CHART_COLOR_NAMES[index % CHART_COLOR_NAMES.length] || CHART_COLOR_NAMES[0],
    chart: {
      id: insight.gender,
      label: getGenderLabel(insight.gender),
      value: insight.total,
      moved: insight.notInitial,
      hired: insight.hired,
    },
  }))

  const firstGender = data[0]?.chart.id
  const hasData = data.length > 1 || (firstGender !== null && firstGender !== undefined)

  return (
    <Card>
      <Card.Title
        tooltip={i18n.t('ats.insights_page.parity_breakdown_metrics.gender_distribution_tooltip')}
      >
        {i18n.t('ats.insights_page.parity_breakdown_metrics.gender_distribution')}
      </Card.Title>
      <Card.Content>
        {alert && (
          <Box width="full">
            <Alert>{alert}</Alert>
          </Box>
        )}
        {!hasData && (
          <DataNotAvailable
            customMessage={i18n.t(
              'ats.insights_page.empty_state.parity_breakdown_metrics.description'
            )}
          />
        )}
        {hasData && (
          <>
            <Box
              flexDirection="row"
              width="full"
              justifyContent="spaceEvenly"
              padding="s16"
              alignItems="center"
            >
              <Box gap="s12">
                {data.map((gender) => (
                  <Box key={gender.chart.id} flexDirection="row" gap="s8">
                    <InfoDot size="medium" color={gender.color} />
                    <Text size="200" color="grey800">
                      {gender.chart.label}
                    </Text>
                  </Box>
                ))}
              </Box>
              <Box height="s200" width="s200" flexGrow>
                <PieChart
                  margin={{ top: 10, right: 0, bottom: 10, left: 0 }}
                  data={data.map((gender) => gender.chart)}
                  centeredMetricLabel={i18n.t('ats.insights_page.parity_breakdown_metrics.total')}
                />
              </Box>
            </Box>

            <Table hideBorder fullWidth noRadius>
              <Table.Row>
                <Table.Cell width={10}></Table.Cell>
                <Table.Cell>
                  {i18n.t('ats.insights_page.parity_breakdown_metrics.table.gender')}
                </Table.Cell>
                <Table.Cell>
                  {i18n.t('ats.insights_page.parity_breakdown_metrics.table.total_candidates')}
                </Table.Cell>
                <Table.Cell>
                  {i18n.t('ats.insights_page.parity_breakdown_metrics.table.moved')}
                </Table.Cell>
                <Table.Cell>
                  {i18n.t('ats.insights_page.parity_breakdown_metrics.table.hired')}
                </Table.Cell>
              </Table.Row>

              {data.map((gender) => (
                <Table.Row key={gender.chart.id}>
                  <Table.Cell>
                    <InfoDot size="medium" color={gender.color} />
                  </Table.Cell>
                  <Table.Cell>{gender.chart.label}</Table.Cell>
                  <Table.Cell>{gender.chart.value}</Table.Cell>
                  <Table.Cell>{gender.chart.moved}</Table.Cell>
                  <Table.Cell>{gender.chart.hired}</Table.Cell>
                </Table.Row>
              ))}
            </Table>
          </>
        )}
      </Card.Content>
    </Card>
  )
}
